import { Tag } from 'antd';
import { Gauge } from 'lucide-react';
import { Link } from 'react-router';
import { Skeleton } from '../../../shared/components/Skeleton';
import { SkeletonRegion } from '../../../shared/components/SkeletonRegion';
import { closeOutTripPath, closeOutTruckPath } from '../distance-agreement.paths';
import { formatKm, formatPercent, stateTag } from '../policies/distance-labels.policy';
import { useTruckDistanceGaps } from './use-truck-distance-gaps';

function DistanceAgreementSkeleton() {
  return (
    <SkeletonRegion label="Loading distance agreement" className="flex items-center gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <Skeleton className="size-9" />
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-40" />
        <Skeleton className="h-3 w-64" />
      </div>
    </SkeletonRegion>
  );
}

/** The truck's trips whose odometer and GPS distances disagreed, for the recent period: a gap that keeps coming back is a signal about the truck. Each opens that trip's comparison. */
export function TruckDistanceAgreementCard({ truckId }: { truckId: string }) {
  const state = useTruckDistanceGaps(truckId);
  if (state.status === 'loading') return <DistanceAgreementSkeleton />;
  if (state.status === 'unavailable') return null;

  const { count, items, periodDays } = state.gaps;
  return (
    <section aria-label="Distance agreement" className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex items-center gap-3">
        <div className="grid size-9 place-items-center rounded-lg bg-primary/10 text-primary">
          <Gauge className="size-4" />
        </div>
        <div>
          <p className="font-medium text-fg">{count === 0 ? 'No distance gaps' : `${count} ${count === 1 ? 'trip' : 'trips'} with a distance gap`}</p>
          <p className="text-sm text-muted">Odometer against GPS, trips closed out in the last {periodDays} days.</p>
        </div>
      </div>
      {count > 0 && (
        <ul className="flex flex-col divide-y divide-line">
          {items.map((item) => {
            const tag = stateTag(item.state);
            return (
              <li key={item.tripId} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <Link to={closeOutTripPath(item.tripId)} className="font-medium">
                  {item.number}
                </Link>
                <span className="text-sm text-muted">
                  {item.gapKm === null ? 'Not available' : formatKm(item.gapKm)}
                  {item.gapPercent !== null && ` (${formatPercent(item.gapPercent)})`}
                </span>
                <Tag color={tag.color}>{tag.label}</Tag>
              </li>
            );
          })}
        </ul>
      )}
      {count > items.length && (
        <Link to={closeOutTruckPath(truckId)} className="text-sm">
          See all {count} trips
        </Link>
      )}
    </section>
  );
}
